/**
 * The weekly scorecard and HELM's own outcome measures (A7Y-3872, D10-D12).
 * Every tile and measure shows the exact definition text from the contract
 * (`HELM_SCORECARD_DEFINITIONS`/`HELM_MEASURE_DEFINITIONS`) so Yevhen can see
 * — and overrule — how each figure is derived, per the epic. A `null` value
 * (no checkpoint yet, or a failed Slack search for maker mornings) reads
 * "unverified this run" rather than a fabricated zero.
 */
import { HELM_MEASURE_DEFINITIONS, HELM_SCORECARD_DEFINITIONS } from '@a7y/platform-health/helm';
import { HELM_SOURCE_IDS } from '@a7y/platform-health/helm';
import type {
  HelmMeasures,
  HelmScorecard,
  HelmSourceHealth,
  HelmSourceId,
} from '@a7y/platform-health/helm';
import { HelmSection } from './HelmSection';

const UNVERIFIED = 'unverified this run';

/**
 * Freshness per state (A7Y-5873: Trends read "stale 8" from the measure the run
 * stored while Runs read all eight Fresh). Today's page passes the source health
 * Runs shows, so both tabs give one value per source; a past run has none and
 * keeps the measure it stored.
 */
function freshnessValue(
  measures: HelmMeasures,
  sourceHealth?: Record<HelmSourceId, HelmSourceHealth>
): string {
  const count = (state: HelmSourceHealth['state']) =>
    sourceHealth
      ? HELM_SOURCE_IDS.filter((id) => sourceHealth[id]?.state === state).length
      : measures.freshness[state].length;
  return `fresh ${count('fresh')} · stale ${count('stale')} · partial ${count('partial')} · unavailable ${count('unavailable')}`;
}

function Tile({ label, value, definition }: { label: string; value: string; definition: string }) {
  return (
    <div
      data-testid="helm-scorecard-tile"
      className="rounded-md border border-zinc-200 p-2 dark:border-zinc-700"
    >
      <div className="text-xs uppercase text-zinc-500">{label}</div>
      <div className="text-lg font-semibold text-zinc-900 dark:text-zinc-50">{value}</div>
      <p className="mt-1 text-xs text-zinc-500">{definition}</p>
    </div>
  );
}

function Measure({
  testId,
  label,
  value,
  definition,
  headline,
}: {
  testId: string;
  label: string;
  value: string;
  definition: string;
  headline?: boolean;
}) {
  return (
    <li
      data-testid={testId}
      className={headline ? 'font-semibold text-red-600 dark:text-red-400' : undefined}
    >
      {label}: {value}
      <p className="text-xs font-normal text-zinc-500">{definition}</p>
    </li>
  );
}

export function Scorecard({
  scorecard,
  measures,
  sourceHealth,
}: {
  scorecard: HelmScorecard | null;
  measures: HelmMeasures | null;
  /** Today's source health, as Runs shows it (A7Y-5873); absent for a past run. */
  sourceHealth?: Record<HelmSourceId, HelmSourceHealth>;
}) {
  return (
    <HelmSection title="Scorecard" lineOn="none">
      <div className="grid grid-cols-2 gap-3">
        <Tile
          label="Decisions"
          value={
            scorecard?.decisions
              ? `arrived ${scorecard.decisions.arrived} · cleared ${scorecard.decisions.cleared}`
              : UNVERIFIED
          }
          definition={HELM_SCORECARD_DEFINITIONS.decisions}
        />
        <Tile
          label="Maker mornings"
          value={
            scorecard?.makerMornings
              ? `held ${scorecard.makerMornings.held} of ${scorecard.makerMornings.of}`
              : UNVERIFIED
          }
          definition={HELM_SCORECARD_DEFINITIONS.makerMornings}
        />
        <Tile
          label="Open board items"
          value={scorecard?.openBoardItems != null ? String(scorecard.openBoardItems) : UNVERIFIED}
          definition={HELM_SCORECARD_DEFINITIONS.openBoardItems}
        />
        <Tile
          label="Warm activation"
          value={
            scorecard?.warmActivation
              ? `${scorecard.warmActivation.weeksSince}w since ${scorecard.warmActivation.lastOn}`
              : UNVERIFIED
          }
          definition={HELM_SCORECARD_DEFINITIONS.warmActivation}
        />
      </div>

      <h4 className="mt-6 mb-2 text-xs font-medium uppercase tracking-wide text-zinc-500">
        HELM&apos;s own measures
      </h4>
      {measures ? (
        <ul className="space-y-1 text-sm">
          <Measure
            testId="helm-measure-missed-critical"
            headline
            label="Missed critical"
            value={String(measures.missedCritical.count)}
            definition={HELM_MEASURE_DEFINITIONS.missedCritical}
          />
          <Measure
            testId="helm-measure-incorrect-repeated-reminders"
            label="Incorrect repeated reminders"
            value={String(measures.incorrectRepeatedReminders.count)}
            definition={HELM_MEASURE_DEFINITIONS.incorrectRepeatedReminders}
          />
          <Measure
            testId="helm-measure-correction-persistence"
            label="Correction persistence"
            value={`${measures.correctionPersistence.held} / ${measures.correctionPersistence.corrections}`}
            definition={HELM_MEASURE_DEFINITIONS.correctionPersistence}
          />
          <Measure
            testId="helm-measure-evidence-coverage"
            label="Evidence coverage"
            value={`${measures.evidenceCoverage.linked} / ${measures.evidenceCoverage.claims}`}
            definition={HELM_MEASURE_DEFINITIONS.evidenceCoverage}
          />
          <Measure
            testId="helm-measure-delivery"
            label="Delivery"
            value={`on time ${measures.delivery.onTime} · late ${measures.delivery.late} · missed ${measures.delivery.missed} · nudges sent ${measures.delivery.nudgesSent} · failed ${measures.delivery.nudgesFailed}`}
            definition={HELM_MEASURE_DEFINITIONS.delivery}
          />
          <Measure
            testId="helm-measure-freshness"
            label="Freshness"
            value={freshnessValue(measures, sourceHealth)}
            definition={HELM_MEASURE_DEFINITIONS.freshness}
          />
        </ul>
      ) : (
        <p className="text-sm text-zinc-500">{UNVERIFIED}</p>
      )}
    </HelmSection>
  );
}
